import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { usePageParam } from "@/hooks/use-page-param";
import { ListPagination } from "@/components/ui/list-pagination";
import { Plus, Radar, Trash2 } from "lucide-react";

import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  useIndicacoesAgencia,
  useRemoveIndicacao,
} from "@/hooks/use-indicacoes-agencia";
import type { Agencia } from "@/types/agencia";
import { normalizeString } from "@/utils/searchUtils";

import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import { SearchInput } from "@/components/ui/search-input";
import { FilterToolbar } from "@/components/ui/filter-toolbar";
import { EmptyState, ErrorState, LoadingState } from "@/components/ui/states";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { IndicarAgenciaDialog } from "@/components/freelancers/IndicarAgenciaDialog";
import { DisponibilidadeLinha } from "@/components/freelancers/DisponibilidadeBadge";
import { useDisponibilidadeBatch } from "@/hooks/useDisponibilidade";

const TODAS = "__todas__";

/**
 * Tela única de gestão do "Radar de Perfis": lista todos os matches manuais
 * (freelancer ↔ agência), com filtros e adicionar/remover num só lugar.
 */
export default function RadarAgencias() {
  const { toast } = useToast();
  const { data: indicacoes = [], isLoading, isError, refetch } = useIndicacoesAgencia();
  const remove = useRemoveIndicacao();
  // PERF: busca só os freelancers que aparecem no radar (ids dos matches),
  // em vez de carregar a tabela inteira (~5,5k) via cache.
  const indFreelancerIds = useMemo(
    () => [...new Set(indicacoes.map((i) => i.freelancer_id))],
    [indicacoes],
  );
  const { data: freelancersRadar = [] } = useQuery({
    queryKey: ["radar-freelancers", indFreelancerIds.join(",")],
    enabled: indFreelancerIds.length > 0,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("freelancers")
        .select("id, nome, email, is_active")
        .in("id", indFreelancerIds);
      if (error) throw error;
      return data ?? [];
    },
  });
  // Disponibilidade unificada (v_availability_summary) em lote, só p/ os ids do
  // radar — mesma verdade da listagem/detalhe/assistente. Ausência = "Sem info".
  const { data: dispMap } = useDisponibilidadeBatch(indFreelancerIds);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [busca, setBusca] = useState("");
  const [filtroAgencia, setFiltroAgencia] = useState<string>(TODAS);

  const { data: agencias = [] } = useQuery({
    queryKey: ["agencias"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("agencias")
        .select()
        .order("nome");
      if (error) throw error;
      return (data || []) as Agencia[];
    },
  });

  const nomeAgencia = useMemo(() => {
    const map = new Map<string, string>();
    agencias.forEach((a) => map.set(a.id, a.nome));
    return map;
  }, [agencias]);

  const freelancerById = useMemo(() => {
    const map = new Map<string, { nome: string; is_active: boolean }>();
    freelancersRadar.forEach((f) =>
      map.set(f.id, {
        nome: f.nome || f.email,
        is_active: f.is_active !== false,
      }),
    );
    return map;
  }, [freelancersRadar]);

  const linhasFiltradas = useMemo(() => {
    const buscaNorm = normalizeString(busca);
    return indicacoes.filter((ind) => {
      if (filtroAgencia !== TODAS && ind.agencia_id !== filtroAgencia)
        return false;
      if (buscaNorm) {
        const nome = normalizeString(
          freelancerById.get(ind.freelancer_id)?.nome ?? "",
        );
        if (!nome.includes(buscaNorm)) return false;
      }
      return true;
    });
  }, [indicacoes, filtroAgencia, busca, freelancerById]);

  const [currentPage, setCurrentPage] = usePageParam();
  const PAGE_SIZE = 30;
  const totalPages = Math.max(1, Math.ceil(linhasFiltradas.length / PAGE_SIZE));
  const paginaEfetiva = Math.min(currentPage, totalPages);
  const linhasPagina = linhasFiltradas.slice(
    (paginaEfetiva - 1) * PAGE_SIZE,
    paginaEfetiva * PAGE_SIZE,
  );

  const handleRemove = async (id: string) => {
    try {
      await remove.mutateAsync(id);
      toast({ title: "Match removido" });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Erro ao remover",
        description: (err as Error).message,
      });
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={
          <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight sm:text-3xl">
            <Radar className="h-6 w-6" />
            Radar de Perfis
          </h1>
        }
        subtitle="Matches manuais de freelancers por agência. Use para destacar um perfil quando surgir uma vaga da agência."
        actions={
          <Button onClick={() => setDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Novo match
          </Button>
        }
      />

      <FilterToolbar>
        <SearchInput
          value={busca}
          onChange={setBusca}
          placeholder="Buscar por freelancer..."
        />
        <Select value={filtroAgencia} onValueChange={setFiltroAgencia}>
          <SelectTrigger className="w-full sm:w-[220px]">
            <SelectValue placeholder="Agência" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={TODAS}>Todas as agências</SelectItem>
            {agencias.map((a) => (
              <SelectItem key={a.id} value={a.id}>
                {a.nome}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FilterToolbar>

      {isLoading ? (
        <LoadingState variant="list" />
      ) : isError ? (
        <ErrorState onRetry={() => refetch()} />
      ) : linhasFiltradas.length === 0 ? (
        <EmptyState
          icon={Radar}
          title="Nenhum match encontrado"
          description={
            busca || filtroAgencia !== TODAS
              ? "Ajuste a busca ou o filtro de agência."
              : "Adicione o primeiro match para destacar um perfil quando surgir uma vaga da agência."
          }
        />
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Freelancer</TableHead>
                <TableHead>Disponibilidade</TableHead>
                <TableHead>Agência</TableHead>
                <TableHead>Motivo</TableHead>
                <TableHead className="w-[60px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {linhasPagina.map((ind) => (
                <TableRow key={ind.id}>
                  <TableCell className="font-medium">
                    {(() => {
                      const f = freelancerById.get(ind.freelancer_id);
                      return (
                        <>
                          {f?.nome ?? "Freelancer"}
                          {f && !f.is_active && (
                            <span className="ml-2 text-red-500">inativo</span>
                          )}
                        </>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    <DisponibilidadeLinha
                      summary={dispMap?.get(ind.freelancer_id)}
                      level="inline"
                    />
                  </TableCell>
                  <TableCell>
                    {nomeAgencia.get(ind.agencia_id) ?? "Agência"}
                  </TableCell>
                  <TableCell className="max-w-[280px] truncate text-muted-foreground">
                    {ind.motivo || "-"}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7"
                      onClick={() => handleRemove(ind.id)}
                      disabled={remove.isPending}
                      title="Remover match"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {totalPages > 1 && (
        <ListPagination
          currentPage={paginaEfetiva}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}

      <IndicarAgenciaDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </div>
  );
}
